<template>
  <div class="computer-container">
    <div class="title">计算薪资</div>
    <a-form class="form" ref="formRef" size="large" :model="formData" :label-col-props="{ span: 0 }"
      :wrapper-col-props="{ span: 24 }">
      <a-form-item field="countryCode" :rules="[{ required: true, message: '国家为必填' }]" :validate-trigger="['change']"
        hide-asterisk>
        <a-select v-model="formData.countryCode" placeholder="请选择" allow-search>
          <a-option v-for="c in concatCountryList" :key="c.name" :value="c.code">{{
            c.desc
          }}</a-option>
          <template #prefix>
            <div class="amount-prefix">
              国家
            </div>
          </template>
        </a-select>
      </a-form-item>
      <a-form-item field="amount" :rules="[
        { required: true, message: '年度薪资为必填' },
        { type: 'number', maxLength: 6, max: 1000000, message: '年度薪资不能大于100万' },
        { type: 'number', min: 0, message: '年度薪资不能小于0' }
      ]" :validate-trigger="['change']" hide-asterisk model-event="input">
        <a-input-number v-model="formData.amount" hide-button placeholder="请输入（默认使用美元换算）">
          <template #prefix>
            <div class="amount-prefix">
              年度薪资
            </div>
          </template>
        </a-input-number>
      </a-form-item>
    </a-form>
    <a-button class="btn" type="primary" :loading="btnLoading" @click="handleCalc">开始计算</a-button>
    <div class="tip">{{ tip }}</div>
  </div>
</template>

<script setup lang="ts">
import { countryList, deelCountryList } from '@/common/global';
import { FormInstance } from '@arco-design/web-vue';

interface IFormData {
  amount?: number
  countryCode: string
}
interface IProps {
  showDeelCountry?: boolean
  tip: string
}

const props = defineProps<IProps>();
const route = useRoute();
const btnLoading = ref(false);
const formData = reactive<IFormData>({
  amount: undefined,
  countryCode: ''
});

const concatCountryList = computed(() => {
  return props.showDeelCountry ? countryList.concat(deelCountryList) : countryList;
});

const formRef = ref<FormInstance | null>(null);
const handleCalc = async () => {
  const res = await formRef.value?.validate();
  if (res) {
    return false;
  }
  const countryName = concatCountryList.value?.find(c => c.name === formData.countryCode)?.desc;
  useLogger().track('calculator', {
    country: countryName,
    salary: formData.amount
  });
  btnLoading.value = true;
  await navigateTo({
    path: route.path,
    query: {
      ...route.query,
      ...formData
    },
  });
  window.location.reload();
};

onMounted(() => {
  formData.amount = Number(route.query.amount) || undefined;
  formData.countryCode = route.query.countryCode as string || '';
});

</script>

<style lang="less" scoped>
.computer-container {
  width: 680px;
  height: 350px;
  background: #FFFFFF;
  border-radius: 12px;
  margin: -50px auto 0px;
  overflow: hidden;

  .title {
    font-weight: 500;
    font-size: 24px;
    line-height: 34px;
    text-align: center;
    color: #1D2129;
    margin-top: 32px;
  }

  .form {
    width: 400px;
    margin: 0 auto;
    margin-top: 28px;

    :deep(.arco-row) {

      .arco-input-wrapper,
      .arco-select {
        background-color: #fff;
        border: 1px solid #C9CDD4;
        border-radius: 4px;
        padding: 0px 12px 0px 20px;

        .arco-input,
        .arco-select-view-input,
        .arco-select-view-value {
          font-size: 16px;

          &::placeholder {
            color: #86909C;
            font-size: 16px;
          }
        }

        .arco-select-view-prefix,
        .arco-input-prefix {
          padding-right: 43px;
          position: relative;

          .amount-prefix {
            font-weight: 400;
            font-size: 18px;
            line-height: 22px;
            color: #1D2129;
          }

          &::after {
            content: "";
            position: absolute;
            width: 1px;
            height: 20px;
            background-color: #E5E6EB;
            right: 23px;
          }
        }
      }
    }
  }

  .btn {
    width: 400px;
    height: 48px;
    margin: 12px auto;
    display: block;
    font-weight: 500;
    font-size: 16px;
    border-radius: 4px;
  }

  .tip {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    color: #86909C;
  }
}
@media screen and (max-width: 800px) {
  .computer-container {
    width: calc(100% - 32px);
    margin: -49px auto 0;
    padding: 0 16px 24px;
    box-sizing: border-box;
    height: auto;
    .title {
      font-size: 20px;
      line-height: 28px;
      margin-top: 24px;
    }
    .form {
      margin-top: 24px;
      width: 100%;
      :deep(.arco-form-item-content-wrapper) {
        .arco-select-view-single, .arco-input-wrapper {
          height: 48px;
          .arco-select-view-prefix, .arco-input-prefix {
            padding-right: 24px;
            &::after {
              right: 11px;
            }
            .amount-prefix {
              font-size: 16px;
            }
          }
        }
      }
      :deep(.arco-form-item) {
        margin-bottom: 16px;
      }
    }
    .btn {
      width: 100%;
      margin-top: 8px;
    }
  }
}
</style>
